// @ts-strict-ignore
import produce from 'immer';
import stableStringify from 'json-stable-stringify';
import isObject from 'lodash/isObject';
import memoize from 'lodash/memoize';

import {
  ExpansionToggleLabelMode,
  Section,
  SectionItem,
  SubLabelTheme,
  TooltipInfo,
} from 'common/components/items-menu/types';
import {getIconForEvent, renameEvent} from 'common/report/util/events';
import {baseComparator} from 'common/util';
import {hasUserFilters, isAutoTrackCustomEvent} from 'common/util/custom_events';
import {EventDefinition, MpEvent} from 'common/util/definitions-types';
import {
  CUSTOM_EVENT_TAG,
  DROPPABLE_MIXPANEL_EVENTS,
  Permission,
  ProjectFeatureFlag,
  SESSION_END_EVENT_NAME,
  SESSION_START_EVENT_NAME,
  SPECIAL_EVENT_VALUES,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {customEventsStore} from 'common/report/stores/custom-events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Store} from 'common/report/stores/store';
import {getEventsReportUrlForEvent, getProjectId, getWorkspaceId} from 'common/report/util';
import {lexicalCompose} from 'common/util/function';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForEvent} from 'common/widgets/context-panel/util';
import {assertUnreachable} from 'common/util/typescript';
import {PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import {reporter} from 'common/report/error-reporting';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {OrderBy} from 'common/util/array';
import {PATH_SEPARATOR} from 'common/constants';
import {projectHasEventApprovalTurnedOn} from 'common/util/event-approval';
import {SPECIAL_EVENT_CONTEXT_TOOLTIP_DATA} from 'common/report/special-event-context-tooltip-data';
import type {ProjectUsersResource} from 'common/report/model-resources/project-users';
import {getAllEventsDisabledTooltip} from 'common/report/insights/util/events';
import {classifyEvents} from 'common/report/queries/app-api/classify-events-client';
import {Placement} from 'common/components/tooltip/types';
import {mapTemplateFieldToEventType, TemplateField} from 'common/widgets/rhs-panel/types';

import {EventItem, EventMenuItem, EventTagMenuItem} from '../events-menu/types';
import {sortItemsByLabel} from '../utils/util';
import {MenuItemType, SortBy} from '../properties-menu/types';
import {SPECIAL_ORDER_EVENTS} from '../events-menu/constants';
import {EventSection, EventSectionItem, PropertySectionItem} from '../properties-menu/item-menu-types';
import {BaseDataController} from './base-controller';

const orderByLabel = lexicalCompose(
  baseComparator<{label?: string}, string>({transform: (item) => item.label && item.label.toLowerCase()}),
);

const maxNumRecommendedEvents = 5;

export type EventsControllerSectionConfig = {
  dataGroupIds?: Set<string | null>;
  disableAllEventsReason?: string;
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  excludeUndroppableEvents?: boolean;
  hideEditControls?: boolean;
  searchFilter?: string;
  selected?: EventItem;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  mainSectionShowLimit?: number;
  sortByValue?: SortBy;
  recentlyUsedEvents?: EventItem[];
  subLabel?: string;
  mainSectionLabel?: string;
  tagFilters?: string[];
  otherOptions?: Array<PropertySectionItem<string>>;
  searchVerified?: boolean;
  suggestedEventType?: string;
};

export class EventsController extends BaseDataController {
  protected onStoreCallback = () => {
    this.updateEventItems();
    this.getContextPanelData.cache.clear?.();
  };
  public data = new Map<string, SectionItem<EventItem>>();
  public loading: boolean = undefined;
  private hasClassifiedEvents = false;
  // Org-level "Enable Mixpanel AI" toggle. Pushed in by consumers that have
  // access to CurrentProject (e.g. events-menu). When false, both the
  // /classify call and the Suggested Events section are suppressed.
  private aiEnabled = false;

  public setAIEnabled(enabled: boolean): void {
    const wasEnabled = this.aiEnabled;
    this.aiEnabled = enabled;
    // If AI just became enabled and we haven't classified yet, kick off
    // classification now so suggestions can populate. Without this, consumers
    // that mount after fetchData() has already run would never trigger /classify.
    if (enabled && !wasEnabled && !this.hasClassifiedEvents && typeof this.loading !== `undefined`) {
      this.classifyAndRefresh();
    }
  }

  private classifyAndRefresh(): void {
    const params = {projectId: getProjectId(), workspaceId: getWorkspaceId()};
    classifyEvents(getProjectId()).then(() => {
      this.hasClassifiedEvents = true;
      if (eventDefinitionsStore.hasFetchedValue()) {
        eventDefinitionsStore[`refresh`](params);
      } else {
        eventDefinitionsStore[`fetch`](params);
      }
    });
  }

  get providerStores(): Array<Store<unknown>> {
    return [customEventsStore, eventDefinitionsStore];
  }

  public canWriteCustomEvents(): boolean {
    return Boolean(currentProjectStore.value?.hasPermission(Permission.WriteCustomEvents));
  }

  private loadData(mode: `fetch` | `refresh`) {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.data = new Map();

    const params = {projectId: getProjectId(), workspaceId: getWorkspaceId()};

    if (!this.hasClassifiedEvents && this.aiEnabled) {
      this.classifyAndRefresh();
    }

    customEventsStore[mode](params);
    eventDefinitionsStore[mode](params);

    this.updateComponents();
  }

  public fetchData() {
    this.loadData(`fetch`);
  }

  public refreshData() {
    this.loadData(`refresh`);
  }

  /**
   * Recovers from a stale loading state where loading is stuck as `true`
   * because store callbacks were missed (e.g., during SPA navigation).
   * If stores already have data, forces an update. Otherwise resets loading
   * so a fresh fetch can be triggered.
   */
  public recoverIfStaleLoading() {
    if (!this.loading) {
      return;
    }

    const allStoresInitialized = this.providerStores.every((store) => store.hasFetchedValue());
    if (allStoresInitialized) {
      // Stores have data but callback was missed — force update
      this.updateEventItems();
    } else {
      this.loading = false;
      this.fetchData();
    }
  }

  public getSections(sectionConfig: EventsControllerSectionConfig = {}): Array<Section<EventMenuItem>> {
    return [this.getVisibleSection(sectionConfig), this.getHiddenSection(sectionConfig)];
  }

  prepAndSortItems(
    eventItems: Array<SectionItem<EventItem>>,
    additionalItems: Array<SectionItem<EventItem>>,
    sortByValue: SortBy,
    searchFilter: string,
    recentlyUsedEvents: EventItem[],
  ): Array<SectionItem<EventMenuItem>> {
    if (!sortByValue) {
      // old menu only
      return [...additionalItems, ...eventItems];
    }

    let events = [];
    switch (sortByValue) {
      case SortBy.Relevance: {
        events = this.sortEventsByScore(
          [...eventItems, ...additionalItems].filter(Boolean),
          recentlyUsedEvents.filter(Boolean),
          searchFilter,
        );
        break;
      }
      case SortBy.NameAsc:
        events = [...sortItemsByLabel([...additionalItems, ...eventItems].filter(Boolean), {order: OrderBy.Ascending})];
        break;
      case SortBy.NameDesc:
        events = [
          ...sortItemsByLabel([...additionalItems, ...eventItems].filter(Boolean), {order: OrderBy.Descending}),
        ];
        break;
      default:
        assertUnreachable(sortByValue);
    }
    let sortedEvents = events.filter((item) => isNaN(SPECIAL_ORDER_EVENTS.get(item.value?.value)));

    events
      .filter((item) => !isNaN(SPECIAL_ORDER_EVENTS.get(item.value?.value)))
      .forEach((item) => {
        const order = SPECIAL_ORDER_EVENTS.get(item.value?.value);
        sortedEvents = [...sortedEvents.slice(0, order), item, ...sortedEvents.slice(order)];
      });
    return sortedEvents;
  }

  getHiddenSection(sectionConfig: EventsControllerSectionConfig): Section<EventItem> {
    const eventDefinitions = Object.values(eventDefinitionsStore.value);

    const items = this.filterAndMapEventItems(
      eventDefinitions.filter((def) => def.hidden).map(getDataDefinitionsEventMenuItem),
      sectionConfig,
    );

    const eventApprovalInUse = projectHasEventApprovalTurnedOn(currentProjectStore.value);

    const eventApprovalTooltip = eventApprovalInUse
      ? {
          text: `New events are hidden by default. \nContact your project admin or owner to change event visibility. \n[Learn More](https://docs.mixpanel.com/docs/admin/data-governance/event-approval)`,
          'max-width': `200px`,
        }
      : null;

    return {
      items,
      label: `Hidden Events`,
      hideTopDivider: true,
      expansionToggle: {
        initialIsExpanded: false,
        labelMode: ExpansionToggleLabelMode.ShowHide,
        ...(eventApprovalInUse && {
          labelSuffix: {
            icon: `info`,
            tooltip: eventApprovalTooltip,
          },
        }),
      },
    };
  }

  getVisibleSection(sectionConfig: EventsControllerSectionConfig): Section<EventMenuItem> {
    const {
      disableAllEventsReason,
      searchFilter,
      selected,
      showAllEvents,
      showAnyEvent,
      showSpecialDatasetProperty,
      showTopEvents,
      mainSectionShowLimit = 0,
      sortByValue = null,
      recentlyUsedEvents = [],
      subLabel = null,
      mainSectionLabel = ({t}) => t(`query-builder.events-menu.section-all-events`),
      otherOptions = [],
    } = sectionConfig;

    const eventItems = this.filterAndMapEventItems(Array.from(this.data.values()), sectionConfig);

    const createItemFromSpecialEvent = (value: string): SectionItem<EventItem> => {
      const label = renameEvent(value);
      return {
        icon: `custom-event-beta`,
        label,
        value: {value, label},
        isSelected: value === selected?.value,
        subLabel,
        subLabelTheme: subLabel ? SubLabelTheme.Breadcrumb : null,
        description: value === SPECIAL_EVENTS.ALL_EVENTS ? `All events, excluding hidden events` : undefined,
      };
    };

    // TODO probably makes sense to make this more like the properties menu where we have a constants file
    // rather than 1 off logic for stuff
    const additionalItems: Array<SectionItem<EventItem>> = [
      showAnyEvent && createItemFromSpecialEvent(SPECIAL_EVENTS.ANY_EVENT),
      showAllEvents && {
        ...createItemFromSpecialEvent(SPECIAL_EVENTS.ALL_EVENTS),
        isDisabled: !!disableAllEventsReason,
        tooltip: disableAllEventsReason && getAllEventsDisabledTooltip(disableAllEventsReason),
      },
      /** @todo [jakub] address top events issue, only allow top events if not a rollup project */
      showTopEvents && !showSpecialDatasetProperty && createItemFromSpecialEvent(SPECIAL_EVENTS.TOP_EVENTS),
      ...(otherOptions.length
        ? otherOptions
            .map((option) => SPECIAL_EVENT_VALUES.includes(option.value) && createItemFromSpecialEvent(option.value))
            .filter(Boolean)
        : []),
    ].filter(Boolean);

    return {
      label: mainSectionLabel,
      isLoading: this.loading,
      items: this.prepAndSortItems(eventItems, additionalItems, sortByValue, searchFilter, recentlyUsedEvents),
      showLimit: mainSectionShowLimit,
      hideTopDivider: true,
    };
  }

  // TODO: This function is feature gated and waiting for API support to be enabled
  getSuggestedEventsSection(sectionConfig: EventsControllerSectionConfig): Section<EventMenuItem> | null {
    if (!this.aiEnabled) {
      return null;
    }

    const {searchFilter, recentlyUsedEvents = [], suggestedEventType} = sectionConfig;

    // Filter events that have suggested flag set to true
    const allEvents = Array.from(this.data.values());

    const filteredEvents = allEvents.filter((eventData) => {
      const eventKey = eventData.value?.name || eventData.value?.value;
      const eventDefinition = eventDefinitionsStore.value[eventKey];
      return (
        eventDefinition &&
        eventDefinition.event_type === mapTemplateFieldToEventType(suggestedEventType as TemplateField)
      );
    });

    const suggestedEventDefinitions = filteredEvents.sort((a, b) => {
      const eventKeyA = a.value?.name || a.value?.value;
      const eventKeyB = b.value?.name || b.value?.value;
      const rankA = eventDefinitionsStore.value[eventKeyA]?.event_type_rank ?? Infinity;
      const rankB = eventDefinitionsStore.value[eventKeyB]?.event_type_rank ?? Infinity;
      return rankA - rankB;
    });

    const maxSelectedTooltip: TooltipInfo = {
      text: ({t}) => t(`query-builder.events-menu.section-suggested-events-info`),
      placement: Placement.Top,
    };

    // Return null if no suggested events
    if (suggestedEventDefinitions.length === 0) {
      return null;
    }

    // Map to event items
    const eventItems = this.filterAndMapEventItems(suggestedEventDefinitions, sectionConfig);

    return {
      key: `suggested-events`,
      label: ({t}) => t(`query-builder.events-menu.section-suggested-events`),
      sectionLeftIcon: `spark`,
      icon: `info`,
      tooltip: maxSelectedTooltip,
      isLoading: this.loading,
      items: this.prepAndSortItems(eventItems, [], null, searchFilter, recentlyUsedEvents).slice(
        0,
        maxNumRecommendedEvents,
      ),
      hideTopDivider: true,
    };
  }

  getEventTagsSection({
    excludeCustomEvents = false,
    showLimit = 2,
  }: {
    excludeCustomEvents?: boolean;
    showLimit?: number;
  }): Section<EventTagMenuItem> {
    const tagCounts: {[tagName: string]: number} = {};
    for (const eventKey of this.data.keys()) {
      eventDefinitionsStore.value[eventKey]?.tags
        .filter(() => !(excludeCustomEvents && this.data?.get(eventKey)?.value?.custom))
        .forEach((tag) => (tagCounts[tag] = tagCounts[tag] ? tagCounts[tag] + 1 : 1));
    }

    const eventStageTagMenuItems: Array<SectionItem<EventTagMenuItem>> = []; // TODO: our sorting functions breaks type inference
    for (const tagName in tagCounts) {
      const count = tagCounts[tagName];
      eventStageTagMenuItems.push({
        label: tagName,
        value: {
          value: tagName,
          count,
        },
      });
    }

    return {
      label: `Events tagged as`,
      items: eventStageTagMenuItems.sort(orderByLabel),
      showLimit,
    };
  }

  getCustomEventItems(excludeCustomEvents = false): Array<SectionItem<EventItem>> {
    if (excludeCustomEvents) {
      return [];
    }

    const customEvents = Object.values(customEventsStore.value)
      // entity-sharing : hide custom events that user does not have access to from menus
      .filter((ce) => ce.can_view);

    return customEvents.map((ce) => {
      return getCustomEventMenuItem(ce, eventDefinitionsStore.value[`$custom_event:${ce.id}`]);
    });
  }

  getVisibleEventsItems(): Array<SectionItem<EventItem>> {
    let eventItems: Array<SectionItem<EventItem>> = [];

    // we filter out custom events here to prevent double count. custom events from data definitions store & custom events store are stitched together in getCustomEventItems
    // eventually, we want to abstract the stitching to the definitions store
    eventItems = Object.values(eventDefinitionsStore.value)
      .filter((ed) => !ed.customEventId && !ed.hidden)
      .map(getDataDefinitionsEventMenuItem);
    return eventItems;
  }

  updateEventItems() {
    let items = [...this.getVisibleEventsItems(), ...this.getCustomEventItems()];
    items = items.sort(orderByLabel);

    this.loading = false;
    this.data = new Map(items.map((item) => [item.value?.value, item]));
    this.updateComponents();
  }

  /** filters out events based on dropdown configuration */
  filterAndMapEventItems(
    eventItems: Array<SectionItem<EventItem>>,
    {
      dataGroupIds,
      excludeAutotrackEvents,
      excludeCustomEvents,
      excludeUndroppableEvents,
      selected,
      showSessionEvent,
      showSpecialDatasetProperty,
      subLabel,
      tagFilters = [],
      searchVerified = false,
    }: EventsControllerSectionConfig = {},
  ): Array<SectionItem<EventItem>> {
    // Check if there are any non-null data group IDs
    const hasNonNullDataGroupId = dataGroupIds ? Array.from(dataGroupIds).some((id) => id !== null) : false;
    showSessionEvent = !hasNonNullDataGroupId && !showSpecialDatasetProperty && showSessionEvent;

    const customEvents = Object.values(customEventsStore.value);
    let processedEventItems = eventItems.filter(({value: eventItem}) => {
      const matchesTagFilter: boolean = tagFilters.every(
        (tagFilter) =>
          (tagFilter === CUSTOM_EVENT_TAG && eventItem?.custom) ||
          (eventItem?.tags && eventItem.tags.includes(tagFilter)),
      );

      if (!matchesTagFilter) {
        return false;
      }

      if (searchVerified && !eventItem?.verified) {
        return false;
      }

      if (eventItem?.custom) {
        if (excludeCustomEvents) {
          return false;
        }

        const customEvent = customEventsStore.value[eventItem?.id];
        // NOTE: not sure if the `customEvent` guard here is necessary. Is it possible for a custom event to be in
        // eventDefinitionsStore but not in customEventsStore? Added because it was causing some tests to fail.
        if (customEvent) {
          // filters out auto track custom events
          if (excludeAutotrackEvents) {
            return !isAutoTrackCustomEvent(customEvent, customEvents);
            // filters out custom events with user filters when looking at a different data group
          } else if (hasNonNullDataGroupId) {
            return !hasUserFilters(customEvent, customEvents);
          }
        }

        return true;
      }

      // filters out session events from event definitions when session events are not supported
      if ([SESSION_START_EVENT_NAME, SESSION_END_EVENT_NAME].includes(eventItem?.value)) {
        return showSessionEvent && !hasNonNullDataGroupId;
      }

      // filters out events that cannot be dropped
      if (excludeUndroppableEvents) {
        const eventDef = eventDefinitionsStore.value[eventItem?.value];
        if (eventDef) {
          return (
            (!eventDef.isMixpanelDefinition || DROPPABLE_MIXPANEL_EVENTS[eventDef.name]) &&
            !eventDef.merged &&
            !eventDef.dropped
          );
        }
      }

      return true;
    });

    processedEventItems = processedEventItems.map((item) => {
      return {
        ...item,
        subLabel,
        subLabelTheme: subLabel ? SubLabelTheme.Breadcrumb : null,
        icon: getIconForEvent(item.value),
        verified: item.value?.verified,
        lastVerified: item.value?.lastVerified,
        lastVerifiedBy: item.value?.lastVerifiedBy,
      };
    });

    const hasSelectedValue = selected?.value ?? false;
    if (hasSelectedValue) {
      processedEventItems = processedEventItems.map((event) => ({
        ...event,
        isSelected: event.value?.value === selected?.value,
      }));
    }

    return processedEventItems;
  }

  public getSampleEventUrl(item: EventItem): string {
    if (!isObject(item)) {
      reporter.warn(`unexpected type for eventItem`, item);
      return ``;
    }
    // Ignore tag and invalid items
    if (item?.value?.type === `tag` || typeof item.value !== `string`) {
      return ``;
    }
    return getEventsReportUrlForEvent({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
      event: item,
      presetDateRange: PresetDateRange.Last30Days,
    });
  }

  public getContextPanelData = memoize(this._getContextPanelData, (item, options) => stableStringify({item, options}));
  public _getContextPanelData(
    item: EventItem,
    {
      projectUsers,
      allowEdit = false,
      throughPropertiesMenu = false,
    }: {
      projectUsers: ProjectUsersResource;
      allowEdit?: boolean;
      throughPropertiesMenu?: boolean;
    },
  ): ContextPanelData {
    const canWriteDataDefinition = currentProjectStore.value?.hasPermission(Permission.WriteDataDefinitions);
    const eventDefinitions = Object.values(eventDefinitionsStore.value);

    let tooltipData: ContextPanelData = null;
    if (item) {
      tooltipData = getTooltipDataForEvent(item as unknown as MpEvent, eventDefinitions, {
        projectUsers,
        allowEdit: allowEdit && this.canWriteCustomEvents(),
        allowDescriptionEdit: canWriteDataDefinition,
        sampleEventUrl: this.getSampleEventUrl(item),
      });
      if (SPECIAL_EVENT_VALUES.includes(item.value)) {
        const specialTooltipData = SPECIAL_EVENT_CONTEXT_TOOLTIP_DATA.find(
          (data) => data.entityName === item.value && (!data.condition || data.condition()),
        );
        tooltipData = specialTooltipData || tooltipData;
      }

      // Move `getTooltipDataForEvent` if simple menu advances out of beta
      if (tooltipData) {
        tooltipData = produce(tooltipData, (data) => {
          if (tooltipData?.entityIcon === `custom`) {
            data.entityIcon = `custom-event-beta`;
            data.subHeader = ({t}) => t(`query-builder.metrics.custom-event`);
          } else if (SPECIAL_EVENT_VALUES.includes(item.value)) {
            data.entityIcon = `custom-event-beta`;
            data.subHeader = ({t}) => t(`query-builder.metrics.custom-event`);
            data.extraInfo = [({t}) => t(`query-builder.context-menu.automatically-created`)];
          }
        });
      }
    }

    // default tooltipData for entries that do not have definitions
    // We do not want to show context panel on hovering `Select event`
    if (tooltipData === null && item?.label !== `Select event` && !item?.itemFromInput && !throughPropertiesMenu) {
      tooltipData = {
        header: item?.label,
        subHeader: null,
        path: item?.subLabel ? [item.subLabel.split(PATH_SEPARATOR)[0]] : null,
        entityIcon: item?.icon,
        description: ``,
        editItem: null,
        metrics: [],
        details: [],
        links: [],
        actions: [],
        isRecentlyUsed: item?.isRecentlyUsed,
      };
    }

    return tooltipData;
  }

  private isEventRecentlyUsed(item: EventItem, recentlyUsedEvents: EventItem[]) {
    const recentlyUsedUniqueKeys = new Set(recentlyUsedEvents.map((eventItem) => eventItem.value));
    return recentlyUsedUniqueKeys.has(item.value);
  }

  private sortEventsByScore(
    events: Array<SectionItem<EventItem>>,
    recentlyUsedEvents: EventItem[],
    searchFilter,
  ): Array<SectionItem<EventItem>> {
    const searchScores = {};
    let maxSearchScore = 0;
    let maxPopularity = 0;
    events.forEach((item) => {
      // todo: support fuzzy matching for localized event names

      const searchScore =
        (searchFilter && typeof item.label === `string` && fuzzyMatch(item.label, searchFilter)?.score) || 0;
      searchScores[item.value?.value] = searchScore;
      if (searchScore > maxSearchScore) {
        maxSearchScore = searchScore;
      }

      const popularity = Number(item.uiUniqueQueryCount) || 0;
      if (popularity > maxPopularity) {
        maxPopularity = popularity;
      }
    });

    const SEARCH_SCORE_WEIGHT = 3;
    const RECENTLY_USED_WEIGHT = 2;

    const eventsWithMeta = events.map((sectionItem) => {
      const isRecentlyUsed = this.isEventRecentlyUsed(sectionItem.value, recentlyUsedEvents);
      sectionItem.value.isRecentlyUsed = isRecentlyUsed;
      const recentlyUsed = isRecentlyUsed ? 1 : 0;
      const popularity = maxPopularity ? (Number(sectionItem.uiUniqueQueryCount) || 0) / maxPopularity : 0;
      const searchScore = searchScores[sectionItem.value?.value];
      const normalizedSearchScore = maxSearchScore ? searchScore / maxSearchScore : 0;
      const score = normalizedSearchScore * SEARCH_SCORE_WEIGHT + recentlyUsed * RECENTLY_USED_WEIGHT + popularity;

      if (isNaN(score)) {
        throw Error(`Score is NaN.`);
      }

      sectionItem.score = score;
      return {item: sectionItem, recentlyUsed, popularity, score};
    });

    eventsWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.Debug)) {
      eventsWithMeta.forEach((entry) => {
        const meta = {...entry};
        delete meta.item;
        entry.item.subLabel = JSON.stringify(meta);
      });
    }

    return eventsWithMeta.map((entry) => entry.item);
  }

  getRecentsSection(recentItems: EventItem[]): EventSection {
    const eventData = this.data;
    const items: EventSectionItem[] = recentItems
      .map((recentItem) => {
        const eventItem = eventData.get(recentItem.value);
        if (eventItem) {
          return {
            ...eventItem,
            menuItemType: MenuItemType.Events,
            isRecentlyUsed: true,
          } as EventSectionItem;
        }
        return null;
      })
      .filter(Boolean);

    return {
      label: ({t}) => t(`query-builder.events-menu.section-recent-events`),
      items,
      type: MenuItemType.Events,
    };
  }
}

/**
 * Prepare one top event entry for display in mp-items-menu
 * @param {object} topEvent - top event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
/**
 * Prepare one custom event entry for display in mp-items-menu
 * @param {object} customEvent - custom event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getCustomEventMenuItem(customEvent, eventDefinition?: EventDefinition): SectionItem<EventItem> {
  return {
    icon: `custom-event-beta`,
    label: customEvent.name,
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: {
      isCollectEverything: customEvent.is_collect_everything_event,
      custom: true,
      id: customEvent.id,
      label: customEvent.name,
      value: `$custom_event:${customEvent.id}`,
      tags: eventDefinition?.tags,
      verified: eventDefinition?.verified,
      lastVerified: eventDefinition?.lastVerified,
      lastVerifiedBy: eventDefinition?.lastVerifiedBy?.name || eventDefinition?.lastVerifiedBy?.email,
    },
  };
}

export function getDataDefinitionsEventMenuItem(eventDefinition: EventDefinition): SectionItem<EventItem> {
  const isCustomEvent = Boolean(eventDefinition.customEventId);
  const eventItem: EventItem = {
    label: isCustomEvent ? eventDefinition.name : renameEvent(eventDefinition.name),
    value: isCustomEvent ? `$custom_event:${eventDefinition.customEventId}` : eventDefinition.name,
    ...(isCustomEvent ? {id: eventDefinition.customEventId, custom: true} : {}),
    tags: eventDefinition.tags,
    verified: eventDefinition.verified,
    lastVerified: eventDefinition.lastVerified,
    lastVerifiedBy: eventDefinition.lastVerifiedBy?.name || eventDefinition.lastVerifiedBy?.email,
  };
  return {
    icon: getIconForEvent(eventItem),
    label: eventItem.label,
    searchableContent: eventDefinition?.name,
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: eventItem,
  };
}
